var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { FlashList } from '@shopify/flash-list';
import { useInfiniteQuery } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import styled from 'styled-components/native';
import { SccPressable } from '@/components/SccPressable';
import SccRemoteImage from '@/components/SccRemoteImage';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import useAppComponents from '@/hooks/useAppComponents';
import useNavigation from '@/navigation/useNavigation';
import SearchLoading from '@/screens/SearchScreen/components/SearchLoading';
const ESTIMATED_ITEM_HEIGHT = 200;
export default function SavedContentsTab() {
    var _a;
    const navigation = useNavigation();
    const { api } = useAppComponents();
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, } = useInfiniteQuery({
        queryKey: ['SavedContents'],
        queryFn: (_a) => __awaiter(this, [_a], void 0, function* ({ pageParam }) {
            return (yield api.listSavedContents({
                nextToken: pageParam !== null && pageParam !== void 0 ? pageParam : undefined,
                limit: 20,
            })).data;
        }),
        getNextPageParam: lastPage => {
            var _a;
            return (_a = lastPage.nextToken) !== null && _a !== void 0 ? _a : undefined;
        },
        initialPageParam: undefined,
    });
    const items = (_a = data === null || data === void 0 ? void 0 : data.pages.flatMap(page => { var _a; return (_a = page.items) !== null && _a !== void 0 ? _a : []; })) !== null && _a !== void 0 ? _a : [];
    const handleItemPress = useCallback((item) => {
        navigation.navigate('Webview', { url: item.sccContent.url });
    }, [navigation]);
    if (isLoading) {
        return _jsx(SearchLoading, {});
    }
    if (isError) {
        return (_jsx(NoResultContainer, { children: _jsx(NoResultText, { children: "\uC800\uC7A5\uD55C \uCEE8\uD150\uCE20\uB97C \uBD88\uB7EC\uC62C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4." }) }));
    }
    if (items.length === 0) {
        return (_jsx(NoResultContainer, { children: _jsx(NoResultText, { children: "\uC800\uC7A5\uD55C \uCEE8\uD150\uCE20\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4." }) }));
    }
    return (_jsx(ListContainer, { children: _jsx(FlashList, { contentContainerStyle: { backgroundColor: color.white }, data: items, keyExtractor: item => item.id, renderItem: ({ item, index }) => (_jsx(SavedContentItem, { item: item, isFirst: index === 0, onPress: () => handleItemPress(item) })), estimatedItemSize: ESTIMATED_ITEM_HEIGHT, onEndReached: () => {
                if (hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            }, onEndReachedThreshold: 0.5 }) }));
}
function SavedContentItem({ item, isFirst, onPress }) {
    var _a;
    const { sccContent } = item;
    const webPageDetail = sccContent.webPageDetail;
    const imageUrls = (_a = webPageDetail === null || webPageDetail === void 0 ? void 0 : webPageDetail.imageUrls) !== null && _a !== void 0 ? _a : [];
    const displayedImages = imageUrls.slice(0, 5);
    const extraImageCount = Math.max(0, imageUrls.length - 5);
    return (_jsx(SccPressable, { elementName: "saved-content-item", logParams: { sccContentId: sccContent.id }, onPress: onPress, children: _jsxs(ItemContainer, { isFirst: isFirst, children: [(webPageDetail === null || webPageDetail === void 0 ? void 0 : webPageDetail.title) ? (_jsx(ItemTitle, { numberOfLines: 2, children: webPageDetail.title })) : (_jsx(ItemTitle, { numberOfLines: 2, children: sccContent.url })), (webPageDetail === null || webPageDetail === void 0 ? void 0 : webPageDetail.description) ? (_jsx(ItemDescription, { numberOfLines: 2, children: webPageDetail.description })) : null, displayedImages.length === 0 ? null : displayedImages.length < 5 ? (_jsx(ItemImageRow, { children: displayedImages.map((url, i) => (_jsx(SccRemoteImage, { imageUrl: url, resizeMode: "cover", wrapperBackgroundColor: null, style: { width: 72, height: 72, borderRadius: 6 } }, `${url}-${i}`))) })) : (_jsx(ItemImageRow, { children: displayedImages.map((url, i) => (_jsxs(ItemImageCell, { children: [_jsx(SccRemoteImage, { imageUrl: url, resizeMode: "cover", wrapperBackgroundColor: null, style: StyleSheet.absoluteFillObject }), i === displayedImages.length - 1 && extraImageCount > 0 ? (_jsx(ItemImageOverlay, { children: _jsxs(ItemImageOverlayText, { children: ["+", extraImageCount] }) })) : null] }, `${url}-${i}`))) }))] }) }));
}
const ListContainer = styled(View) `
  flex: 1;
`;
const NoResultContainer = styled.View `
  flex-grow: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;
const NoResultText = styled.Text `
  font-size: 14px;
  font-family: ${() => font.pretendardMedium};
  padding-bottom: 120px;
  color: ${() => color.gray50};
`;
const ItemContainer = styled.View `
  padding: 16px 20px;
  background-color: ${color.white};
  border-top-width: ${({ isFirst }) => (isFirst ? '0' : '1px')};
  border-top-color: #f2f2f5;
`;
const ItemTitle = styled.Text `
  font-size: 17px;
  font-family: ${() => font.pretendardBold};
  color: ${color.brand50};
  line-height: 24px;
  margin-bottom: 4px;
`;
const ItemDescription = styled.Text `
  font-size: 13px;
  font-family: ${() => font.pretendardRegular};
  color: ${color.gray70};
  line-height: 20px;
  margin-bottom: 12px;
`;
const ItemImageRow = styled.View `
  flex-direction: row;
  gap: 4px;
`;
const ItemImageCell = styled.View `
  flex: 1;
  aspect-ratio: 1;
  border-radius: 6px;
  overflow: hidden;
  background-color: ${color.gray15};
  position: relative;
`;
const ItemImageOverlay = styled.View `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.45);
  align-items: center;
  justify-content: center;
`;
const ItemImageOverlayText = styled.Text `
  color: ${color.white};
  font-size: 14px;
  font-family: ${() => font.pretendardSemibold};
`;
